import React from 'react'

import { Dashboard, PageLayout, SideSummary } from '@components/common'
import { LeavingGuard } from '@components/utils/LeavingGuard'
import {
  BackButton,
  Checkbox,
  Dropdown,
  Fieldset,
  HeadContent,
  Input,
  InputUI,
  Loading,
  RadioButton,
  Subsection,
  Tag,
  ToolbarButton,
} from '@fourthwall/components'
import { ArrowShortDownIcon, DotsHorizontalIcon } from '@fourthwall/icons'
import { ConfirmBar } from '@modules/Dashboard/modules/ConfirmBar'
import { routing } from '@utils/routing'

import { BadgePreview } from '../../components/BadgePreview'
import { getEmojiIcon } from '../../utils'
import { AddAnotherRule } from './components/AddAnotherRule'
import { AddAnotherStreak } from './components/AddAnotherStreak'
import { AddCustomStyling } from './components/AddCustomStyling'
import { BadgeType } from './components/BadgeType'
import { RulesSeparator } from './components/RulesSeparator'
import { SelectRule } from './components/SelectRule'
import { SelectedMembers } from './components/SelectedMembers'
import { SelectedTeamMembers } from './components/SelectedTeamMembers'
import { StreakRule } from './components/StreakRule'
import { Summary } from './components/Summary'
import { UsernameStyling } from './components/UsernameStyling'
import { useBadgeContext } from './hooks/useBadgeContext'
import { FormFields } from './hooks/useBadgeForm/types'
import { BadgeProvider } from './providers/BadgeProvider/BadgeProvider'

const BadgeContent = () => {
  const {
    badge,
    tiers,
    form,
    availableRules,
    hasEmptyRule,
    hasAnyRules,
    hasAvailableRules,
    usersQuery,
    isFetching,
    isLoading,
    enableBadge,
    disableBadge,
    deleteBadge,
    getRuleComponent,
  } = useBadgeContext()

  if (!badge || isFetching) return null

  return (
    <Loading isLoading={isLoading}>
      <HeadContent title={badge.text} />
      <PageLayout
        leftContent={
          <>
            <Subsection>
              <div style={{ display: 'grid', gap: 8 }}>
                {badge.type === 'MemberStreakDefault' && (
                  <BadgePreview
                    text="New member"
                    emoji={getEmojiIcon(form.values.emoji)}
                    backgroundColor={form.values.backgroundColor}
                    borderColor={form.values.borderColor}
                    textColor={form.values.textColor}
                    withUsername
                    iconOnly={form.values.badgeType === 'IconOnly'}
                    usernameTextColor={
                      form.values.useCustomUsernameColor ? form.values.usernameTextColor : undefined
                    }
                  />
                )}
                <BadgePreview
                  text={form.values.text}
                  emoji={getEmojiIcon(form.values.emoji)}
                  backgroundColor={form.values.backgroundColor}
                  borderColor={form.values.borderColor}
                  textColor={form.values.textColor}
                  verified={badge.type === 'Verified'}
                  iconOnly={form.values.badgeType === 'IconOnly'}
                  withUsername
                  usernameTextColor={
                    form.values.useCustomUsernameColor ? form.values.usernameTextColor : undefined
                  }
                />
              </div>
            </Subsection>
            <Subsection title="Badge styling">
              <Fieldset>
                <Fieldset.Row>
                  <Fieldset.Item>
                    <Input
                      label="Badge text"
                      {...form.getFieldProps(FormFields.text)}
                      disabled={badge.type === 'MemberStreakDefault'}
                    />
                  </Fieldset.Item>
                </Fieldset.Row>
                <BadgeType form={form} />
              </Fieldset>
            </Subsection>
            {(badge.type === 'Custom' || badge.type === 'Verified') && (
              <UsernameStyling form={form} verified={badge.type === 'Verified'} />
            )}
            {badge.type === 'Verified' && (
              <Subsection title="Badge settings">
                <Fieldset>
                  <Fieldset.Row>
                    <RadioButton
                      label="Apply to all internal team staff"
                      {...form.getFieldProps(FormFields.verifiedApplyToType)}
                      checkedValue="All"
                    />
                  </Fieldset.Row>
                  <Fieldset.Row>
                    <RadioButton
                      label="Apply to selected internal team staff"
                      {...form.getFieldProps(FormFields.verifiedApplyToType)}
                      checkedValue="Selected"
                    />
                  </Fieldset.Row>
                  <SelectedTeamMembers form={form} usersQuery={usersQuery} />
                </Fieldset>
              </Subsection>
            )}
            {badge.type === 'Custom' && (
              <Subsection title="Assign badge">
                <Fieldset>
                  <Fieldset.Row>
                    <Checkbox
                      label="This badge can be earned by members"
                      {...form.getFieldProps(FormFields.canBeEarnedByMembers)}
                    />
                  </Fieldset.Row>
                  {form.values.canBeEarnedByMembers && (
                    <>
                      {!hasAnyRules && (
                        <Fieldset.Row inner>
                          <SelectRule form={form} options={availableRules} />
                        </Fieldset.Row>
                      )}
                      {form.values.rules.map((rule, index) => (
                        <React.Fragment key={index}>
                          <Fieldset.Row inner>{getRuleComponent(rule)}</Fieldset.Row>
                          {index === form.values.rules.length - 1 ? null : (
                            <Fieldset.Row inner>
                              <RulesSeparator />
                            </Fieldset.Row>
                          )}
                        </React.Fragment>
                      ))}
                      {hasAvailableRules && hasAnyRules && !hasEmptyRule && (
                        <AddAnotherRule form={form} />
                      )}
                      <Fieldset.Row inner>
                        <Checkbox
                          label="Remove badge when requirements are no longer met"
                          {...form.getFieldProps(FormFields.removeWhenRulesNoLongerMet)}
                        />
                      </Fieldset.Row>
                      {form.errors.rules && typeof form.errors.rules === 'string' && (
                        <Fieldset.Row inner>
                          <InputUI.Error $withoutMargin>{form.errors.rules}</InputUI.Error>
                        </Fieldset.Row>
                      )}
                    </>
                  )}
                  <Fieldset.Row>
                    <Checkbox
                      label="Give badge to selected members"
                      {...form.getFieldProps(FormFields.giveToSelectedMembers)}
                    />
                  </Fieldset.Row>
                  <SelectedMembers form={form} />
                  <Fieldset.Row>
                    <Checkbox
                      label="Give badge to selected internal team staff"
                      {...form.getFieldProps(FormFields.giveToSelectedTeamStaff)}
                    />
                  </Fieldset.Row>
                  <SelectedTeamMembers form={form} usersQuery={usersQuery} />
                  {form.errors.canBeEarnedByMembers && (
                    <Fieldset.Row>
                      <InputUI.Error $withoutMargin>
                        {form.errors.canBeEarnedByMembers}
                      </InputUI.Error>
                    </Fieldset.Row>
                  )}
                </Fieldset>
              </Subsection>
            )}
            {badge.type === 'MemberStreakDefault' && (
              <Subsection title="Customize styling for different streak lengths">
                <Fieldset>
                  <Fieldset.Row>
                    Set custom styling for different lengths of membership streaks. These custom
                    styles will override the default streak badge styling.
                  </Fieldset.Row>

                  {form.values.memberStreakBadges.length === 0 && <AddCustomStyling form={form} />}
                  {form.values.memberStreakBadges.map((memberStreakBadge, index) => (
                    <>
                      <Fieldset.Row key={index}>
                        <StreakRule
                          memberStreakBadge={{ badge: memberStreakBadge, index }}
                          form={form}
                        />
                      </Fieldset.Row>
                      {index === form.values.memberStreakBadges.length - 1 ? null : (
                        <Fieldset.Row>
                          <RulesSeparator withLabel={false} />
                        </Fieldset.Row>
                      )}
                    </>
                  ))}
                  {form.values.memberStreakBadges.length > 0 && <AddAnotherStreak form={form} />}
                </Fieldset>
              </Subsection>
            )}
          </>
        }
        rightContent={
          <SideSummary>
            <SideSummary.Item
              title={
                {
                  Custom: 'Rules for who gets badge',
                  MemberStreakDefault: 'Membership streak badge',
                  MemberStreakCustom: 'Membership streak badge',
                  Verified: 'Verified badge',
                }[badge.type]
              }
            >
              {badge.type === 'Custom' && <Summary form={form} tiers={tiers} />}
              {badge.type === 'Verified' &&
                'Verified badge automatically appears next to internal team staff usernames. You can manage team members under Team settings.'}
              {badge.type == 'MemberStreakDefault' &&
                'The streak badge dynamically shows how many months paid members have been subscribed for.'}
            </SideSummary.Item>
          </SideSummary>
        }
      >
        <Dashboard.Section
          name={badge.text}
          topContent={<BackButton label="Badges" to={routing.memberships.settings.badges.self} />}
          toolbarContent={
            <>
              <Dropdown
                content={
                  <Dropdown.Item
                    label={badge.active ? 'Disable' : 'Enable'}
                    onClick={badge.active ? disableBadge : enableBadge}
                  />
                }
                position="bottom-end"
              >
                <div>
                  <Tag
                    label={badge.active ? 'Live' : 'Disabled'}
                    appearance={badge.active ? 'success' : 'alert'}
                    icon={ArrowShortDownIcon}
                    hoverable
                    size="large"
                  />
                </div>
              </Dropdown>

              {badge.type === 'Custom' && (
                <Dropdown
                  content={<Dropdown.Item label="Delete" onClick={deleteBadge} />}
                  position="bottom-end"
                >
                  <ToolbarButton label="Settings" icon={DotsHorizontalIcon} />
                </Dropdown>
              )}
            </>
          }
          titleSpacing="medium"
          bottomMargin="large"
        />
      </PageLayout>
      <ConfirmBar
        isOpen={form.dirty}
        isLoading={isLoading}
        onConfirm={form.submitForm}
        onCancel={form.resetForm}
      />
      <LeavingGuard when={form.dirty} isValid={form.isValid} onSave={form.submitForm} />
    </Loading>
  )
}

export const Badge = () => (
  <BadgeProvider>
    <BadgeContent />
  </BadgeProvider>
)
